import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getRelatedTitles, getSpecialsForTitle, getTitleById, typeMetaMap } from "@/lib/titles";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TitleCard from "@/components/feature/TitleCard";
import RatingStars from "@/components/base/RatingStars";
import Reveal from "@/components/base/Reveal";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const title = getTitleById(id);
  const [shareTip, setShareTip] = useState(false);

  useSeo({
    title: title ? `${title.name} - WW我的快乐在哪里剧情详情` : "内容不存在 - WW我的快乐在哪里",
    description: title
      ? `${title.name}（${title.year}·${title.region}）：${title.short} ${title.synopsis.slice(0, 60)}…`
      : "抱歉，未找到对应的影视作品。",
    keywords: title
      ? `${title.name},${title.name}剧情,${title.genres.join(",")},WW我的快乐在哪里,剧情介绍`
      : "WW我的快乐在哪里",
    path: `https://91zytv.asia/detail/${id ?? ""}`,
  });

  if (!title) {
    return (
      <section className="mx-auto flex w-full max-w-page flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-file-unknow-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-6 text-2xl font-bold text-foreground-950">没有找到这部作品</h1>
        <p className="mt-3 text-sm text-foreground-600">链接可能已失效，返回首页或搜索其他影视内容吧。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap"
          >
            <i className="ri-home-4-line" aria-hidden="true" />
            返回首页
          </Link>
          <Link
            to="/search"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
          >
            <i className="ri-search-line" aria-hidden="true" />
            去搜索
          </Link>
        </div>
      </section>
    );
  }

  const meta = typeMetaMap[title.type];
  const related = getRelatedTitles(title, 4);
  const specials = getSpecialsForTitle(title);
  const bannerImage = title.backdrop ?? title.cover;

  const metaRows = [
    { icon: "ri-calendar-line", label: "上映时间", value: title.releaseDate },
    { icon: "ri-price-tag-3-line", label: "类型", value: title.genres.join(" / ") },
    { icon: "ri-user-star-line", label: "导演", value: title.director },
    { icon: "ri-group-line", label: "主演", value: title.cast.join("、") },
    { icon: "ri-time-line", label: "片长 / 集数", value: title.duration },
    { icon: "ri-global-line", label: "地区", value: title.region },
  ];

  return (
    <>
      <section className="mx-auto w-full max-w-page px-4 pt-6 md:px-6">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: meta.label, to: `/category/${title.type}` },
            { label: title.name },
          ]}
        />
      </section>

      <section className="relative mt-4 w-full overflow-hidden bg-primary-950">
        <img
          src={bannerImage}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/70" />
        <div className="relative mx-auto flex w-full max-w-page flex-col gap-7 px-4 py-10 md:flex-row md:px-6 md:py-12">
          <div className="mx-auto w-40 flex-none overflow-hidden rounded-lg ring-1 ring-background-50/20 sm:w-48 md:mx-0 md:w-56">
            <img
              src={title.cover}
              alt={`${title.name} 封面`}
              title={`${title.name} - WW我的快乐在哪里剧情详情`}
              className="aspect-[3/4] h-full w-full object-cover object-top"
            />
          </div>

          <div className="flex-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-500 px-3 py-1 text-xs font-medium text-background-50">
              {meta.label}
            </span>
            <h1 className="mt-3 text-3xl font-black text-background-50 md:text-4xl">{title.name}</h1>
            <p className="mt-3 text-sm text-background-200 md:text-base">{title.short}</p>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-sm font-bold text-primary-950">
                <i className="ri-star-fill" aria-hidden="true" />
                {title.rating.toFixed(1)}
              </span>
              <span className="text-sm text-background-200">
                {title.year} · {title.region} · {title.duration}
              </span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {title.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-50/25 px-3 py-1 text-xs text-background-100"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#plot"
                className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400 whitespace-nowrap"
              >
                <i className="ri-book-open-line" aria-hidden="true" />
                阅读剧情简介
              </a>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShareTip((open) => !open)}
                  className="inline-flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" aria-hidden="true" />
                  分享
                </button>
                {shareTip && (
                  <span className="absolute left-0 top-full z-10 mt-2 w-52 rounded-md bg-background-50 px-3 py-2 text-xs text-foreground-700 shadow-none ring-1 ring-background-200">
                    演示按钮：暂未接入真实分享服务
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div>
            <div id="plot">
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
                剧情简介
              </h2>
              <p className="mt-4 whitespace-pre-line text-sm leading-8 text-foreground-700 md:text-base">
                {title.synopsis}
              </p>
            </div>

            <div className="mt-10">
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="h-6 w-1.5 rounded-full bg-secondary-500" aria-hidden="true" />
                影评精选
              </h2>
              <div className="mt-5 space-y-4">
                {title.reviews.map((review) => (
                  <article
                    key={review.id}
                    className="rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                        {review.user.slice(0, 1)}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground-950">{review.user}</p>
                        <div className="mt-0.5 flex items-center gap-2">
                          <RatingStars value={review.rating} />
                          <span className="text-xs text-foreground-500">{review.date}</span>
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">{review.content}</p>
                  </article>
                ))}
              </div>
              <p className="mt-3 text-xs text-foreground-500">* 以上影评均为虚构演示内容，不代表真实用户评价。</p>
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base font-bold text-foreground-950">影片信息</h2>
              <dl className="mt-4 space-y-3">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="flex w-24 flex-none items-center gap-1.5 text-foreground-500">
                      <i className={`${row.icon} text-primary-500`} aria-hidden="true" />
                      {row.label}
                    </dt>
                    <dd className="flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 flex items-center justify-between rounded-md bg-background-100 px-4 py-3">
                <span className="text-sm text-foreground-600">综合评分</span>
                <span className="flex items-center gap-1.5 text-lg font-black text-accent-600">
                  <i className="ri-star-fill text-base" aria-hidden="true" />
                  {title.rating.toFixed(1)}
                </span>
              </div>
            </div>

            {specials.length > 0 && (
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="text-base font-bold text-foreground-950">所属专题</h2>
                <ul className="mt-4 space-y-3">
                  {specials.map((special) => (
                    <li key={special.id}>
                      <Link to={`/special/${special.id}`} className="group flex items-center gap-3">
                        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                          <i className="ri-bookmark-3-line" aria-hidden="true" />
                        </span>
                        <span className="flex-1">
                          <span className="block text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                            {special.name}
                          </span>
                          <span className="mt-0.5 block text-xs text-foreground-500">{special.tag}</span>
                        </span>
                        <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="text-base font-bold text-foreground-950">免责声明</h2>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                本站为影视资讯与剧情介绍展示站点，不提供在线播放与下载服务，页面内作品名称、剧情与评分均为虚构演示内容。
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
          <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-6 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            相关推荐
          </h2>
          <p className="mt-2 text-sm text-foreground-600">根据类型与标签为你匹配的相似作品</p>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((item, index) => (
              <Reveal key={item.id} delay={index * 50}>
                <TitleCard item={item} className="h-full" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}